export interface SsoDataState {
  companyName?: string;
  providerName?: string;
  samlIssuerId?: string;
  loginUrl?: string;
  certificate?: string;
  givenName?: string;
  familyName?: string;
  email?: string;
}

export interface IAccountSsoProviderError {
  key: string;
  msg: string;
}

export interface FormReducer {
  type: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
}
export const formDataReducer = (state: SsoDataState, action: FormReducer): SsoDataState => {
  switch (action.type) {
    case 'SET_COMPANY_NAME':
      return { ...state, companyName: action.value };
    case 'SET_PROVIDER_NAME':
      return { ...state, providerName: action.value };
    case 'SET_SAML_ISSUER_ID':
      return {
        ...state,
        samlIssuerId: action.value,
      };
    case 'SET_LOGIN_URL':
      return {
        ...state,
        loginUrl: action.value,
      };
    case 'SET_CERTIFICATE':
      return { ...state, certificate: action.value };
    case 'SET_GIVEN_NAME':
      return { ...state, givenName: action.value };
    case 'SET_FAMILY_NAME':
      return { ...state, familyName: action.value };
    case 'SET_EMAIL':
      return { ...state, email: action.value };
    default:
      return state;
  }
};

export const PROVIDER_ENTRA = 'Microsoft Entra ID';
const PROVIDER_LEGACY_AZURE = 'Azure ID';
export const PROVIDER_OKTA = 'Okta';
export const PROVIDER_AUTH0 = 'Auth0';

export const providerOptions = [
  { label: PROVIDER_ENTRA, value: PROVIDER_ENTRA },
  { label: PROVIDER_OKTA, value: PROVIDER_OKTA },
  { label: PROVIDER_AUTH0, value: PROVIDER_AUTH0 },
];

// Providers saved before the Entra rename are stored as "Azure ID"; show them as Entra.
export const normalizeProviderName = (providerName?: string): string =>
  providerName === PROVIDER_LEGACY_AZURE ? PROVIDER_ENTRA : providerName || '';

export const isEntraProvider = (providerName?: string): boolean =>
  normalizeProviderName(providerName) === PROVIDER_ENTRA;

// The names Microsoft Entra ID gives these claims in the SAML assertion.
export const ENTRA_CLAIM_URIS = {
  givenName: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname',
  familyName: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname',
  email: 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress',
};

// The reducer action for a field, e.g. 'givenName' -> 'SET_GIVEN_NAME'.
const actionFor = (name: string) => SsoFormFields.find((field) => field.name === name).key;

// Field updates to apply when the provider changes on a new configuration: prefill the
// Entra claim URIs into empty fields, and clear them again only if they are untouched.
export const getAttributeNamePrefill = (
  providerName: string,
  formData: SsoDataState
): FormReducer[] => {
  const fields = Object.keys(ENTRA_CLAIM_URIS);
  const allEmpty = fields.every((field) => !formData[field]?.trim());
  const allUntouchedUris = fields.every((field) => formData[field] === ENTRA_CLAIM_URIS[field]);

  if (isEntraProvider(providerName) && allEmpty) {
    return fields.map((field) => ({
      type: actionFor(field),
      value: ENTRA_CLAIM_URIS[field],
    }));
  }
  if (!isEntraProvider(providerName) && allUntouchedUris) {
    return fields.map((field) => ({ type: actionFor(field), value: '' }));
  }
  return [];
};

const LOGIN_URL_DEFAULT_HINT =
  'This is where we will direct users login if they are required to use single sign-on to login.';

const LOGIN_URL_EXAMPLES: Record<string, string> = {
  [PROVIDER_ENTRA]: 'https://login.microsoftonline.com/<tenant-id>/saml2',
  [PROVIDER_OKTA]: 'https://<your-org>.okta.com/app/<app-name>/<app-id>/sso/saml',
  [PROVIDER_AUTH0]: 'https://<tenant>.auth0.com/samlp/<client-id>',
};

export const getLoginUrlExample = (providerName?: string): string | undefined =>
  LOGIN_URL_EXAMPLES[normalizeProviderName(providerName)];

export const getLoginUrlHint = (providerName?: string): string =>
  getLoginUrlExample(providerName)
    ? "Your identity provider's SAML sign-on URL, e.g."
    : LOGIN_URL_DEFAULT_HINT;

export const COMPANY_NAME_HINT =
  '3-32 characters; letters, numbers, hyphens and underscores. This is what your users type to sign in.';
export const COMPANY_NAME_LOCKED_MSG = "Company Name can't be changed after setup.";
export const ATTRIBUTE_NAMES_HINT =
  'Enter each claim name exactly as your identity provider sends it in the SAML assertion, not its source attribute (for example user.mail).';
export const COMPANY_NAME_FORMAT_MSG =
  'Use 3-32 letters, numbers, hyphens or underscores, with no spaces or symbols.';

export type SsoInputMode = 'manual' | 'upload';

export const INPUT_MODE_OPTIONS = {
  firstOption: { id: 'manual', value: 'Fill in manually' },
  secondOption: { id: 'upload', value: 'Upload metadata XML' },
};

// Filled from the uploaded metadata file and read-only while uploading.
export const METADATA_FIELDS = ['samlIssuerId', 'loginUrl', 'certificate'];

// Field updates that fill the metadata fields from a file, or clear them when called
// without values.
export const getMetadataFieldChanges = (values: SsoDataState = {}): FormReducer[] =>
  METADATA_FIELDS.map((name) => ({ type: actionFor(name), value: values[name] ?? '' }));

export const METADATA_FILE_LOADED_MSG =
  'Metadata file loaded. Login URL, certificate and issuer were filled in.';
export const METADATA_FILE_REQUIRED_MSG = "Choose your identity provider's metadata XML file.";
export const METADATA_FILE_UNREADABLE_MSG = 'Couldn´t read the file. Try choosing it again.';
export const METADATA_FILE_HELP = [
  'Microsoft Entra ID: Enterprise applications > your app > Single sign-on > SAML Certificates > Federation Metadata XML',
  'Okta: Applications > your app > Sign On > SAML 2.0 > Metadata URL (open it and save the page as .xml)',
  'Google Workspace: Admin console > Apps > Web and mobile apps > your SAML app > Download metadata',
];

export const SsoFormFields = [
  {
    key: 'SET_COMPANY_NAME',
    name: 'companyName',
    type: 'input',
    label: 'Company Name (sign-in ID) *',
    placeholder: 'e.g. yourcompany',
    hint: COMPANY_NAME_HINT,
  },
  {
    key: 'SET_PROVIDER_NAME',
    name: 'providerName',
    type: 'select',
    label: 'Provider Name *',
    placeholder: 'Select one',
    options: providerOptions,
  },
  {
    key: 'SET_SAML_ISSUER_ID',
    name: 'samlIssuerId',
    type: 'input',
    label: 'SAML Issuer ID *',
    placeholder: 'e.g. https://sts.windows.net/<tenant-id>/',
  },
  {
    key: 'SET_LOGIN_URL',
    name: 'loginUrl',
    type: 'input',
    label: 'Login URL *',
    placeholder: null,
  },
  {
    key: 'SET_CERTIFICATE',
    name: 'certificate',
    type: 'textarea',
    label: 'Certificate *',
    placeholder: null,
    hint: 'This is your Identity Provider`s public certificate used to validate the signature.',
  },
  {
    key: 'SET_GIVEN_NAME',
    name: 'givenName',
    type: 'input',
    label: 'Attribute Names *',
    placeholder: 'e.g. firstName',
  },
  {
    key: 'SET_FAMILY_NAME',
    name: 'familyName',
    type: 'input',
    placeholder: 'e.g. lastName',
  },
  {
    key: 'SET_EMAIL',
    name: 'email',
    type: 'input',
    placeholder: 'e.g. email',
    hint: ATTRIBUTE_NAMES_HINT,
  },
];
